import type { AccountDelta } from '@sol-tx-visualizer/tx-decode';

interface Props {
  deltas: AccountDelta[];
}

export default function AccountDiff({ deltas }: Props) {
  return (
    <div className="overflow-auto max-h-[600px]">
      <table className="w-full text-sm">
        <thead className="bg-slate-900 sticky top-0">
          <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
            <th className="px-4 py-3 font-medium">Account</th>
            <th className="px-4 py-3 font-medium text-right">SOL Delta</th>
            <th className="px-4 py-3 font-medium">Token Changes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {deltas.length === 0 && (
            <tr>
              <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                No account changes detected.
              </td>
            </tr>
          )}
          {deltas.map((d) => (
            <tr key={d.pubkey} className="hover:bg-slate-800/50 transition-colors">
              <td className="px-4 py-3">
                <div className="font-mono text-xs text-slate-300">
                  {d.pubkey.slice(0, 4)}…{d.pubkey.slice(-4)}
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <DeltaBadge delta={d.lamportsDelta / 1e9} unit="SOL" />
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {d.tokenDeltas.length === 0 && (
                    <span className="text-xs text-slate-600">—</span>
                  )}
                  {d.tokenDeltas.map((td, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2 py-0.5 text-[10px]"
                    >
                      <span className="font-mono text-slate-400">
                        {td.mint.slice(0, 4)}…{td.mint.slice(-4)}
                      </span>
                      <DeltaBadge
                        delta={td.uiDelta ?? 0}
                        unit=""
                        compact
                      />
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DeltaBadge({
  delta,
  unit,
  compact = false,
}: {
  delta: number;
  unit: string;
  compact?: boolean;
}) {
  const isPositive = delta > 0;
  const isZero = delta === 0;

  return (
    <span
      className={`font-mono font-medium ${
        isZero
          ? 'text-slate-500'
          : isPositive
          ? 'text-emerald-400'
          : 'text-rose-400'
      } ${compact ? 'text-[10px]' : 'text-xs'}`}
    >
      {isPositive ? '+' : ''}
      {Math.abs(delta).toFixed(compact ? 2 : 6)}
      {unit && ` ${unit}`}
    </span>
  );
}
